<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisTabs from 'dashboard/components-next/LaisTabs.vue';
import SalesDetailCommission from './SalesDetailCommission.vue';
import SalesDetailManager from './SalesDetailManager.vue';
import SalesDetailNucleo from './SalesDetailNucleo.vue';
import SalesDetailParties from './SalesDetailParties.vue';
import SalesDetailProperty from './SalesDetailProperty.vue';
import SalesDetailReceipt from './SalesDetailReceipt.vue';
import SalesDetailSale from './SalesDetailSale.vue';
import SalesDetailSummary from './SalesDetailSummary.vue';
import SalesHistoryTimeline from './SalesHistoryTimeline.vue';

const props = defineProps({
  initialTab: { type: String, default: 'sale' },
  showNucleoLink: { type: Boolean, default: true },
  showSummary: { type: Boolean, default: true },
  tabOrder: { type: Array, default: undefined },
  transaction: { type: Object, required: true },
});

const { t } = useI18n();
const activeTab = ref('sale');
const activeSaleTab = ref('summary');
const tabs = computed(() => {
  const available = [
    { id: 'sale', label: t('SALES.DETAIL.SALE'), icon: 'i-lucide-file-text' },
    {
      id: 'property',
      label: t('SALES.DETAIL.PROPERTY'),
      icon: 'i-lucide-house',
    },
    {
      id: 'manager',
      label: t('SALES.DETAIL.MANAGER'),
      icon: 'i-lucide-user-round',
    },
    { id: 'sellers', label: t('SALES.DETAIL.SELLERS'), icon: 'i-lucide-users' },
    { id: 'buyers', label: t('SALES.DETAIL.BUYERS'), icon: 'i-lucide-users' },
    {
      id: 'nucleo',
      label: t('SALES.DETAIL.NUCLEO.TAB'),
      icon: 'i-lucide-landmark',
    },
    {
      id: 'history',
      label: t('SALES.DETAIL.HISTORY'),
      icon: 'i-lucide-history',
    },
  ];
  if (!props.tabOrder?.length) return available;

  const byId = new Map(available.map(tab => [tab.id, tab]));
  return props.tabOrder.map(id => byId.get(id)).filter(Boolean);
});
const saleTabs = computed(() => [
  {
    id: 'summary',
    label: t('SALES.DETAIL.SALE_TABS.SUMMARY'),
    icon: 'i-lucide-layout-list',
  },
  {
    id: 'receipt',
    label: t('SALES.DETAIL.RECEIPT.TAB'),
    icon: 'i-lucide-file-signature',
  },
  {
    id: 'commission',
    label: t('SALES.DETAIL.COMMISSION.TAB'),
    icon: 'i-lucide-circle-dollar-sign',
  },
]);
const resolvedTab = tab =>
  tabs.value.some(item => item.id === tab) ? tab : tabs.value[0]?.id || 'sale';
const resolvedSaleTab = tab =>
  saleTabs.value.some(item => item.id === tab) ? tab : 'summary';
const applyInitialTab = tab => {
  activeTab.value = resolvedTab(tab);
  activeSaleTab.value = resolvedSaleTab(tab);
};

watch(
  () => props.transaction.id,
  () => {
    applyInitialTab(props.initialTab);
  }
);
watch(
  () => props.initialTab,
  tab => {
    applyInitialTab(tab);
  },
  { immediate: true }
);
</script>

<template>
  <div class="grid gap-4">
    <SalesDetailSummary v-if="showSummary" :transaction="transaction" />
    <LaisTabs
      v-model="activeTab"
      :tabs="tabs"
      :aria-label="t('SALES.DETAIL.TABS_ARIA')"
    >
      <template #sale>
        <LaisTabs
          v-model="activeSaleTab"
          :tabs="saleTabs"
          :aria-label="t('SALES.DETAIL.SALE_TABS_ARIA')"
        >
          <template #summary>
            <SalesDetailSale :transaction="transaction" />
          </template>
          <template #receipt>
            <SalesDetailReceipt :transaction="transaction" />
          </template>
          <template #commission>
            <SalesDetailCommission :transaction="transaction" />
          </template>
        </LaisTabs>
      </template>
      <template #property>
        <SalesDetailProperty :key="transaction.id" :transaction="transaction" />
      </template>
      <template #manager>
        <SalesDetailManager :manager="transaction.manager_details" />
      </template>
      <template #sellers>
        <SalesDetailParties
          :parties="transaction.party_details || []"
          role="vendedor"
          :transaction-id="transaction.id"
        />
      </template>
      <template #buyers>
        <SalesDetailParties
          :parties="transaction.party_details || []"
          role="comprador"
          :transaction-id="transaction.id"
        />
      </template>
      <template #nucleo>
        <SalesDetailNucleo
          :requests="transaction.nucleo_requests || []"
          :show-open-action="showNucleoLink"
        />
      </template>
      <template #history>
        <SalesHistoryTimeline :events="transaction.history || []" />
      </template>
    </LaisTabs>
  </div>
</template>
